import axios from 'axios';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const AUTH_TOKEN_KEY = 'exchange_access_token';

export function getAuthToken(): string | null {
  const fromCookie = getCookie(AUTH_TOKEN_KEY);
  if (fromCookie) return fromCookie;
  // One-time migration from old localStorage storage
  const legacy = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_KEY) : null;
  if (legacy) {
    setCookie(AUTH_TOKEN_KEY, legacy);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return legacy;
  }
  return null;
}

export function setAuthToken(token: string | null): void {
  if (token) setCookie(AUTH_TOKEN_KEY, token);
  else deleteCookie(AUTH_TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface PriceCalculation {
  base_price: number;
  discount: number;
  final_price: number;
  rate_per_usdt?: number;
}

export interface Order {
  id: number;
  uuid: string;
  status: string;
  usdt_amount: number;
  wallet_address: string;
  wallet_network: string;
  amount_rub: number;
  payment_provider?: string;
  payment_url?: string;
  qr_image?: string;
  created_at: string;
  expires_at?: string;
}

export interface PaymentPageData {
  uuid: string;
  status: string;
  amount_rub: number;
  usdt_amount: number;
  wallet_address: string;
  wallet_network: string;
  payment_url?: string;
  qr_code_data?: string;
  qr_image?: string;
  tx_hash?: string | null;
  expires_at?: string;
  created_at: string;
  paid_at?: string | null;
  processing_at?: string | null;
  completed_at?: string | null;
}

export interface OrderStatusPayload {
  uuid: string;
  status: string;
  tx_hash?: string | null;
  paid_at?: string | null;
  processing_at?: string | null;
  completed_at?: string | null;
}

export interface PriceConfig {
  product_type: string;
  base_price: number;
  min_amount: number;
  max_amount: number;
  discount_threshold?: number;
  discount_percent?: number;
  label?: string;
  description?: string;
}

export interface PaymentMethod {
  code: string;
  name: string;
  description?: string;
  icon_url?: string;
  commission_percent: number;
  commission_fixed: number;
  commission_text?: string;
  min_amount: number;
  max_amount?: number;
}

export interface SiteConfig {
  settings: Record<string, string>;
  prices: Record<string, PriceConfig>;
  payment_methods: PaymentMethod[];
  faqs?: { q: string; a: string }[];
}

export interface PromoValidation {
  valid: boolean;
  code: string;
  discount_type: string;
  discount_value: number;
  discount_amount: number;
  final_amount: number;
}

// API functions
export const userApi = {
  authenticate: (telegramUser: TelegramUser) =>
    api.post<TelegramUser>('/users/auth/telegram', telegramUser),
};

export const paymentApi = {
  getProviders: () =>
    api.get('/payments/providers'),

  calculatePrice: (usdtAmount: number) =>
    api.post<PriceCalculation>('/payments/calculate', null, {
      params: { usdt_amount: usdtAmount },
    }),

  simulatePayment: (orderUuid: string) =>
    api.post(`/payments/simulate/${orderUuid}`),
};

export const orderApi = {
  createUsdtOrder: (
    _telegramId: number,
    usdtAmount: number,
    walletAddress: string,
    walletNetwork: string,
    paymentProvider: string,
    email?: string,
    expectedRub?: number
  ) =>
    api.post<Order>('/orders/usdt', {
      usdt_amount: usdtAmount,
      wallet_address: walletAddress,
      wallet_network: walletNetwork,
      payment_provider: paymentProvider,
      email: email || undefined,
      expected_rub: expectedRub,
    }),

  getOrderPaymentPage: (orderUuid: string) =>
    api.get<PaymentPageData>(`/orders/${orderUuid}`),

  checkOrderStatus: (orderUuid: string) =>
    api.get<OrderStatusPayload>(`/orders/${orderUuid}/status`),
};

export const publicApi = {
  getConfig: () =>
    api.get<SiteConfig>('/public/config'),

  getPrices: () =>
    api.get<{ prices: PriceConfig[] }>('/public/prices'),

  getPaymentMethods: () =>
    api.get<{ methods: PaymentMethod[] }>('/public/payment-methods'),

  getSettings: (category?: string) =>
    api.get<{ settings: Record<string, { value: string; type: string; label?: string }> }>(
      '/public/settings',
      { params: category ? { category } : undefined }
    ),

  validatePromo: (code: string, amount: number) =>
    api.post<PromoValidation>('/public/promo/validate', null, {
      params: { code, amount, product_type: 'usdt' },
    }),
};

// --- Account / auth -------------------------------------------------------

export interface AccountUser {
  id: number;
  email: string | null;
  phone_number: string | null;
  full_name: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  is_fully_verified: boolean;
  sumsub_status: string | null;
  kyc_level: number;
  is_admin: boolean;
  created_at: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user: AccountUser;
}

export interface AccountOrderItem {
  uuid: string;
  status: string;
  usdt_amount: number;
  amount_rub: number;
  wallet_address: string;
  wallet_network: string;
  created_at: string;
  paid_at: string | null;
}

export interface OrderHistoryResponse {
  items: AccountOrderItem[];
  total: number;
  page: number;
  page_size: number;
}

export const accountApi = {
  register: (email: string, password: string) =>
    api.post<TokenResponse>('/account/register', { email, password }),

  login: (email: string, password: string) =>
    api.post<TokenResponse>('/account/login', { email, password }),

  me: () => api.get<AccountUser>('/account/me'),

  emailSendCode: (email?: string) =>
    api.post('/account/email/send-code', { email }),

  emailVerify: (code: string) =>
    api.post<AccountUser>('/account/email/verify', { code }),

  phoneStart: (phone_number?: string) =>
    api.post<{ success: boolean; session_id?: string; expires_at?: string; error?: string }>(
      '/account/phone/start',
      { phone_number }
    ),

  phoneSendCode: (session_id: string, phone_number?: string) =>
    api.post('/account/phone/send-code', { session_id, phone_number }),

  phoneVerify: (session_id: string, code: string) =>
    api.post<AccountUser>('/account/phone/verify', { session_id, code }),

  setFullName: (full_name: string) =>
    api.post<AccountUser>('/account/profile/full-name', { full_name }),

  orders: (page = 1, pageSize = 20) =>
    api.get<OrderHistoryResponse>('/account/orders', {
      params: { page, page_size: pageSize },
    }),

  sumsubToken: () => api.post<{ token: string }>('/account/kyc/sumsub/token'),

  wallets: () => api.get<WalletAddressItem[]>('/account/wallets'),

  walletCreate: (payload: {
    address: string;
    network?: string;
    label?: string;
    is_default?: boolean;
  }) => api.post<WalletAddressItem>('/account/wallets', payload),

  walletUpdate: (id: number, payload: { label?: string; is_default?: boolean }) =>
    api.patch<WalletAddressItem>(`/account/wallets/${id}`, payload),

  walletDelete: (id: number) => api.delete(`/account/wallets/${id}`),
};

export interface WalletAddressItem {
  id: number;
  address: string;
  network: string;
  label: string | null;
  is_default: boolean;
  created_at: string;
  last_used_at: string | null;
}

export default api;
